import '.j{display:grid;}.ssr-plugin'
import '.o{grid-template-columns:repeat(3, 1fr);}.ssr-plugin'
import '.d{gap:2rem;}.ssr-plugin'
import '.p{position:absolute;}.ssr-plugin'
import '.q{top:-36px;}.ssr-plugin'
import '.r{left:1rem;}.ssr-plugin'
import '.s{z-index:5;}.ssr-plugin'
import '.t{overflow:hidden;}.ssr-plugin'
import { styled } from 'inlines'
import { PageLayout } from '@app/pages/layout/PageLayout'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { Tag } from '@app/components/core/tag/Tag'
import { useTranslations } from '@i18n/useLanguage'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { faSliders } from '@fortawesome/pro-regular-svg-icons'
import { useQuery } from '@based/sdk/react'
import { ServiceProviderListModel } from 'functions/app/service-provider/list'
import serviceProviderListFunction from 'functions/app/service-provider/list/based.config'
import { ServiceProviderFilter } from 'functions/app/service-provider/list/filters'
import { Card } from '@app/components/blocks/card/Card'
import { useLocation } from 'wouter'
import { pages } from '@app/util/links'
import { Paragraph } from '@app/components/core/typography/Paragraph'
import { SearchField } from '@app/components/blocks/searchBar/SearchField'
import Row from '@app/components/layout/Row'
import { IconButton } from '@app/components/core/button/IconButton'
import { ServiceProviderFilterDrawer } from './ServiceProviderFilterDrawer'
import Column from '@app/components/layout/Column'
import {
  useQueryStates,
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
} from 'nuqs'
import { Avatar } from '@app/components/core/avatar/Avatar'
import defaultBanner from '@app/assets/defaultBanner.png'

export const DiscoverServiceProviders = () => {
  const t = useTranslations().app.discoverServiceProviders
  const [showFilters, setShowFilters] = useState(false)
  const [searchBarInput, setSearchBarInput] = useState('')
  const [filterParams, setFilterParams] = useQueryStates({
    regions: parseAsArrayOf(parseAsInteger),
    specializations: parseAsArrayOf(parseAsInteger),
    search: parseAsString,
  })

  const searchTerm = filterParams.search ?? ''

  // Build filters object from URL params
  const filters: ServiceProviderFilter = useMemo(
    () => ({
      regions: filterParams.regions,
      specializations: filterParams.specializations,
    }),
    [filterParams.regions, filterParams.specializations],
  )

  // Sync search bar input with URL param
  useEffect(() => {
    setSearchBarInput(searchTerm)
  }, [searchTerm])

  const setSearchTerm = useCallback(
    (term: string) => {
      void setFilterParams({ search: term || null })
    },
    [setFilterParams],
  )

  const onFiltersChange = useCallback(
    (newFilters: ServiceProviderFilter) => {
      void setFilterParams({
        regions: newFilters.regions ?? null,
        specializations: newFilters.specializations ?? null,
      })
    },
    [setFilterParams],
  )

  const serviceProvidersQuery = useQuery(serviceProviderListFunction.name, {
    searchTerm: searchTerm,
    filters,
  })
  const serviceProviders: ServiceProviderListModel[] =
    serviceProvidersQuery.data ?? []

  return (
    <>
      <PageLayout gap="2rem">
        <div>
          <Title.H1 style={{ marginBottom: '0.5rem' }}>{t.title}</Title.H1>
          <Text.Medium>{t.description}</Text.Medium>
        </div>

        <Row alignItems="center">
          <SearchField
            placeholder={t.searchPlaceholder}
            searchBarInput={searchBarInput}
            searchTerm={searchTerm}
            setSearchBarInput={setSearchBarInput}
            setSearchTerm={setSearchTerm}
            style={{ maxWidth: '32rem' }}
          />
          <IconButton
            variant="secondary"
            icon={faSliders}
            onClick={() => setShowFilters(!showFilters)}
            style={{
              marginLeft: '1rem',
            }}
          />
        </Row>

        <div className="j o d"
        >
          {serviceProviders.map((serviceProvider) => (
            <DiscoverServiceProvidersCard
              key={serviceProvider.uuid}
              serviceProvider={serviceProvider}
            />
          ))}
        </div>
      </PageLayout>

      <ServiceProviderFilterDrawer
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onFiltersChange={onFiltersChange}
      />
    </>
  )
}

const DiscoverServiceProvidersCard = ({
  serviceProvider,
}: {
  serviceProvider: ServiceProviderListModel
}) => {
  const [, setLocation] = useLocation()
  return (
    <Card
      image={serviceProvider.banner?.src ?? defaultBanner}
      imageStyle={{ height: '12.5rem', borderTopRightRadius: '1.25rem', borderTopLeftRadius: '1.25rem', borderBottomRightRadius: '0', borderBottomLeftRadius: '0' }}
      onClick={() => setLocation(pages.serviceProvider(serviceProvider.slug))}
    >
      {/* Logo Overlay */}
      <div className="p q r s t"
      >
        <Avatar shape="square" size="medium" image={serviceProvider.avatar?.src} labelSource={serviceProvider.name?.charAt(0)} style={{ border: '1px solid var(--shades-blue-50)' }} />
      </div>

      {/* Service Provider Content */}
      <Column style={{ paddingTop: '1.5rem', justifyContent: 'space-between' }}>
        <div>
          {/* Title */}
          <Title.H4 style={{ marginBottom: '0.5rem' }}>
            {serviceProvider.name}
          </Title.H4>

          {/* Description */}
          <Paragraph.Medium
            style={{
              color: 'var(--shades-blue-500)',
              marginBottom: '1.25rem',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {serviceProvider.description}
          </Paragraph.Medium>
        </div>

        {/* Category Tag */}
        <Row gap="0.5rem" style={{ flexWrap: 'wrap' }}>
          {serviceProvider.type?.map((spType) => (
            <Tag
              key={spType.type ?? spType.name}
              variant="blueMuted"
              size="small"
              label={spType.name || spType.type}
            />
          ))}
        </Row>
      </Column>
    </Card>
  )
}
